/**
 * Helpers for the `data-prop-places-sort` / `data-prop-places-search-sort` data-props.
 *
 * The places API decides the order of the origin/destination lists and the widget
 * renders it verbatim. These props let a tenant ask for an alphabetical list instead,
 * with an independent criterion for the initial list and for text search.
 *
 * The alphabetical order is produced **on the client**. `order_by=alphabetical` is sent
 * as well, but it is additive: the API branch that understands it is not deployed yet,
 * and an unrecognized `order_by` is not an error there — the server answers 200 with its
 * default order. Sending the parameter is what will make the server order the whole set
 * before its result ceiling applies, once it ships.
 *
 * The two orders are not identical by construction. The server orders by the place's own
 * name (`LOWER(COALESCE(ascii_display, display))`); the client orders by the label the row
 * actually renders, which depends on `displayType`. They coincide for `state`,
 * `terminal_city` and `only_terminal`, and diverge for `city` and `city_state`, where the
 * label leads with the city. The client sorts last, so the client wins.
 */

import normalize from './normalize';

/**
 * Accepted values for both props, and the literals the places API accepts for `order_by`.
 * Kept in one place on purpose: the `displayType` enum is already duplicated across three
 * files in this repo and that is not worth repeating.
 */
export const PLACES_SORT = Object.freeze({
  POPULARITY: 'popularity',
  ALPHABETICAL: 'alphabetical',
});

/** Group key `groupOptionsBy` falls back to for places with no state. */
export const PLACES_GROUP_FALLBACK = 'other';

const VALID_CRITERIA = [PLACES_SORT.POPULARITY, PLACES_SORT.ALPHABETICAL];

/**
 * Built once, on first use rather than at import time: the module is pulled in eagerly by
 * the widget bootstrap, and building a collator costs ICU initialisation that every tenant
 * would pay for a comparison only the alphabetical criterion ever makes.
 *
 * `sensitivity: 'base'` folds case and accents, which `normalize` does not do completely
 * (it strips only `á é í ó ú`, leaving `ñ` and `ü` sorting after `z`). `numeric` puts
 * "Terminal 2" before "Terminal 10".
 */
let collator = null;
const getCollator = () => {
  if (!collator) collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

  return collator;
};

const toCriterion = raw => (typeof raw === 'string' ? raw.trim().toLowerCase() : '');

/**
 * Whether a raw data-prop value names a supported criterion.
 * Note an empty attribute is *not* valid — it means "unspecified", and SearchHub relies on
 * that to stay quiet for a bare attribute while reporting an actual typo.
 *
 * @param {unknown} raw - Raw data-prop value
 * @returns {boolean} True when the value names a criterion
 */
export function isValidPlacesSort(raw) {
  return VALID_CRITERIA.includes(toCriterion(raw));
}

/**
 * Resolves both props once, applying the default and the inheritance, so no consumer
 * downstream re-derives the coupling.
 *
 * `placesSearchSort` inherits the *resolved* list criterion, not the raw value, so an
 * invalid list value does not leak into search.
 *
 * @param {Object} [params]
 * @param {unknown} [params.placesSort] - Raw `places-sort` value
 * @param {unknown} [params.placesSearchSort] - Raw `places-search-sort` value
 * @returns {{ placesSort: string, placesSearchSort: string }} Resolved criteria
 */
export function resolvePlacesSort({ placesSort, placesSearchSort } = {}) {
  const listCriterion = isValidPlacesSort(placesSort)
    ? toCriterion(placesSort)
    : PLACES_SORT.POPULARITY;

  const searchCriterion = isValidPlacesSort(placesSearchSort)
    ? toCriterion(placesSearchSort)
    : listCriterion;

  return { placesSort: listCriterion, placesSearchSort: searchCriterion };
}

/**
 * Adds `order_by=alphabetical` to a places URL when the criterion calls for it.
 *
 * A no-op for `popularity`, which is what keeps the default path byte-identical. Applied
 * last on purpose: `setQueryParam` overwrites the key, so this overrides the
 * `origin_hits` / `destination_hits` the branches may already have set, without those
 * lines needing to know about it.
 *
 * @param {Object} url - A `createUrl` object
 * @param {string} criterion - A resolved `PLACES_SORT` value
 * @returns {Object} The same url object, for chaining
 */
export function applyPlacesSortParam(url, criterion) {
  if (criterion === PLACES_SORT.ALPHABETICAL) {
    url.setQueryParam('order_by', PLACES_SORT.ALPHABETICAL);
  }

  return url;
}

/**
 * Picks the comparable form of one field, preferring the API's pre-transliterated
 * lowercase column and falling back to normalizing the raw one.
 *
 * `ascii_display` is guarded by a `COALESCE` server-side, but `city_ascii_name` is not and
 * can arrive null.
 */
const fieldKey = (asciiValue, rawValue) => {
  if (typeof asciiValue === 'string' && asciiValue !== '') return asciiValue;
  if (typeof rawValue === 'string' && rawValue !== '') return normalize(rawValue);

  return '';
};

const terminalKey = place => fieldKey(place?.ascii_display, place?.display);
const cityKey = place => fieldKey(place?.city_ascii_name, place?.city_name);
const stateKey = place => fieldKey(null, place?.state);

/**
 * Builds the ordering key for a place, mirroring the label `SelectElement` renders for the
 * active display type so the visible list reads alphabetically.
 *
 * Returned as parts rather than a joined string so each component is compared in turn: a
 * separator would carry its own collation weight and could reorder pairs where one
 * component is a prefix of the other. Each part is derived only in the branch that uses it,
 * since the state field can never take the pre-transliterated shortcut. Places are
 * snake_case here: the payload is only camelized when a place is selected.
 *
 * @param {Object} place - Raw place object from the places API
 * @param {Object} [options]
 * @param {string} [options.displayType] - Active `data-prop-display-type`
 * @param {boolean} [options.isPackagesSearch] - Packages flow hides the terminal name
 * @returns {string[]} Key parts, most significant first
 */
export function getPlaceSortKey(place, { displayType, isPackagesSearch } = {}) {
  switch (displayType) {
    case 'city':
      return isPackagesSearch ? [cityKey(place)] : [cityKey(place), terminalKey(place)];

    case 'city_state':
      return [cityKey(place), stateKey(place), terminalKey(place)];

    case 'terminal_city':
      return [terminalKey(place), cityKey(place)];

    case 'only_terminal':
      return [terminalKey(place)];

    case 'state':
    default:
      return [terminalKey(place), stateKey(place)];
  }
}

/** A place with no usable name. The API sorts these last (`NULLS LAST`); so do we. */
const isBlankKey = key => key[0] === '';

/**
 * Every key in a single sort comes from the same display type, so they all have the same
 * number of parts and each part is a string.
 */
const compareKeys = (a, b) => {
  if (isBlankKey(a) !== isBlankKey(b)) return isBlankKey(a) ? 1 : -1;

  for (let index = 0; index < a.length; index += 1) {
    const result = getCollator().compare(a[index], b[index]);
    if (result !== 0) return result;
  }

  return 0;
};

/**
 * Orders a list of places by the given criterion.
 *
 * Always returns a new array and never mutates the input. For `popularity` it returns the
 * copy untouched, so enabling nothing changes nothing. Keys are derived once per place
 * rather than once per comparison, and `Array#sort` is stable, so ties keep the order the
 * API returned — its popularity ranking.
 *
 * `groupBy` exists for `data-prop-group-places`: making the group field the primary key is
 * what makes `groupOptionsBy` — which emits groups in first-appearance order — produce
 * alphabetical group headings, without a second sort that could drift from this one.
 *
 * @param {Array} places - Places to order
 * @param {Object} [options]
 * @param {string} [options.criterion] - A resolved `PLACES_SORT` value
 * @param {string} [options.displayType] - Active `data-prop-display-type`
 * @param {boolean} [options.isPackagesSearch] - Packages flow
 * @param {string|null} [options.groupBy] - Place field used as the primary key when grouping
 * @returns {Array} A new, ordered array
 */
export function sortPlaces(places, { criterion, displayType, isPackagesSearch, groupBy } = {}) {
  const list = Array.isArray(places) ? [...places] : [];

  if (criterion !== PLACES_SORT.ALPHABETICAL || list.length < 2) return list;

  const decorated = list.map(place => ({
    place,
    group: groupBy ? fieldKey(null, place?.[groupBy]) || PLACES_GROUP_FALLBACK : null,
    key: getPlaceSortKey(place, { displayType, isPackagesSearch }),
  }));

  decorated.sort(
    (a, b) =>
      (a.group === null ? 0 : getCollator().compare(a.group, b.group)) ||
      compareKeys(a.key, b.key)
  );

  return decorated.map(entry => entry.place);
}
